/**
 * Chatbot Page - AI Assistant for Vietnamese students at Guangxi Normal University
 * With conversation history sidebar
 */
import React, { useState, useRef, useEffect } from 'react';
import { chatWithAssistant, getConversations, getConversation, createConversation, deleteConversation } from '../services/api';
import { ChatbotMessage, Conversation, ConversationMessage as ConversationMessageType } from '../types';
import { useAuth } from '../contexts/AuthContext';

const ChatbotPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [currentConversationId, setCurrentConversationId] = useState<number | null>(null);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messages, setMessages] = useState<ChatbotMessage[]>([
    {
      role: 'assistant',
      content: 'Hello! I am an AI Assistant for Vietnamese students at Guangxi Normal University. I can help you answer questions about your studies, campus life, academic matters, and general inquiries. How can I assist you today?',
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<Array<{ type: 'pdf' | 'word'; data: string; name: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load conversations on mount if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadConversations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Load conversation messages when conversation is selected
  useEffect(() => {
    if (currentConversationId && isAuthenticated) {
      loadConversationMessages(currentConversationId);
    }
  }, [currentConversationId, isAuthenticated]);

  const loadConversations = async () => {
    if (!isAuthenticated) return;
    
    try {
      setIsLoadingConversations(true);
      const data = await getConversations(0, 50);
      setConversations(data);
    } catch (err: any) {
      console.error('Error loading conversations:', err);
    } finally {
      setIsLoadingConversations(false);
    }
  };

  const loadConversationMessages = async (conversationId: number) => {
    try {
      const data = await getConversation(conversationId);
      const conversationMessages: ChatbotMessage[] = data.messages.map((msg: ConversationMessageType) => ({
        role: msg.role as 'user' | 'assistant',
        content: msg.content,
      }));
      
      // If no messages, show welcome message
      if (conversationMessages.length === 0) {
        setMessages([
          {
            role: 'assistant',
            content: 'Hello! I am an AI Assistant for Vietnamese students at Guangxi Normal University. I can help you answer questions about your studies, campus life, academic matters, and general inquiries. How can I assist you today?',
          },
        ]);
      } else {
        setMessages(conversationMessages);
      }
    } catch (err: any) {
      console.error('Error loading conversation messages:', err);
      setError('Failed to load conversation messages');
    }
  };

  const handleNewConversation = async () => {
    if (!isAuthenticated) {
      // If not authenticated, just clear the current chat
      setCurrentConversationId(null);
      setMessages([
        {
          role: 'assistant',
          content: 'Hello! I am an AI Assistant for Vietnamese students at Guangxi Normal University. I can help you answer questions about your studies, campus life, academic matters, and general inquiries. How can I assist you today?',
        },
      ]);
      return;
    }

    try {
      const newConv = await createConversation();
      setCurrentConversationId(newConv.id);
      setMessages([
        {
          role: 'assistant',
          content: 'Hello! I am an AI Assistant for Vietnamese students at Guangxi Normal University. I can help you answer questions about your studies, campus life, academic matters, and general inquiries. How can I assist you today?',
        },
      ]);
      await loadConversations(); // Refresh list
    } catch (err: any) {
      console.error('Error creating conversation:', err);
      setError('Failed to create new conversation');
    }
  };

  const handleSelectConversation = (conversationId: number) => {
    setCurrentConversationId(conversationId);
    setError(null);
  };

  const handleDeleteConversation = async (e: React.MouseEvent, conversationId: number) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;

    try {
      await deleteConversation(conversationId);
      if (currentConversationId === conversationId) {
        setCurrentConversationId(null);
        setMessages([
          {
            role: 'assistant',
            content: 'Hello! I am an AI Assistant for Vietnamese students at Guangxi Normal University. I can help you answer questions about your studies, campus life, academic matters, and general inquiries. How can I assist you today?',
          },
        ]);
      }
      await loadConversations();
    } catch (err: any) {
      console.error('Error deleting conversation:', err);
      setError('Failed to delete conversation');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const validFiles = Array.from(files).filter(file => 
      file.type === 'application/pdf' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'application/msword' ||
      file.name.toLowerCase().endsWith('.pdf') ||
      file.name.toLowerCase().endsWith('.docx') ||
      file.name.toLowerCase().endsWith('.doc')
    );
    
    if (validFiles.length === 0) {
      setError('Please select PDF or Word files only');
      return;
    }

    const filePromises = validFiles.map(file => {
      return new Promise<{ type: 'pdf' | 'word'; data: string; name: string }>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          if (event.target?.result) {
            let fileType: 'pdf' | 'word';
            if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
              fileType = 'pdf';
            } else {
              fileType = 'word';
            }
            
            resolve({
              type: fileType,
              data: event.target.result as string,
              name: file.name
            });
          } else {
            reject(new Error('Failed to read file'));
          }
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });

    Promise.all(filePromises)
      .then(fileData => {
        setSelectedFiles(prev => [...prev, ...fileData]);
      })
      .catch(err => {
        setError('Failed to load files: ' + err.message);
      });

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    if ((!inputMessage.trim() && selectedFiles.length === 0) || isLoading) return;

    // Create new conversation if not authenticated and no conversation selected
    if (!isAuthenticated) {
      // Just send without saving
    } else if (!currentConversationId) {
      // Create new conversation before sending
      try {
        const newConv = await createConversation();
        setCurrentConversationId(newConv.id);
        await loadConversations();
      } catch (err: any) {
        console.error('Error creating conversation:', err);
        setError('Failed to create conversation. Message not saved.');
      }
    }

    const hasFiles = selectedFiles.length > 0;
    const fileDescription = hasFiles 
      ? `[${selectedFiles.map(f => f.type === 'pdf' ? 'PDF' : 'Word').join(', ')}]`
      : '';

    const userMessage: ChatbotMessage = {
      role: 'user',
      content: inputMessage.trim() || fileDescription,
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    const messageToSend = inputMessage.trim();
    const filesToSend = [...selectedFiles];
    setInputMessage('');
    setSelectedFiles([]);
    setIsLoading(true);
    setError(null);

    try {
      const conversationHistory = updatedMessages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      const pdfs = filesToSend.filter(f => f.type === 'pdf').map(f => ({
        name: f.name,
        data: f.data
      }));
      const wordFiles = filesToSend.filter(f => f.type === 'word').map(f => ({
        name: f.name,
        data: f.data
      }));

      let finalMessage = messageToSend;
      if (pdfs.length === 0 && wordFiles.length === 0 && !finalMessage) {
        finalMessage = 'Please help me';
      } else if (pdfs.length > 0 && wordFiles.length === 0 && !finalMessage) {
        finalMessage = 'Please analyze these PDF files';
      } else if (pdfs.length === 0 && wordFiles.length > 0 && !finalMessage) {
        finalMessage = 'Please analyze these Word files';
      }

      const response = await chatWithAssistant({
        message: finalMessage,
        conversation_id: currentConversationId || undefined,
        conversation_history: conversationHistory,
        pdfs: pdfs.length > 0 ? pdfs : undefined,
        word_files: wordFiles.length > 0 ? wordFiles : undefined,
        model: 'qwen-max',
        provider: 'dashscope',
      });

      setMessages([
        ...updatedMessages,
        {
          role: 'assistant',
          content: response.response,
        },
      ]);

      // Refresh conversations list to update message count
      if (isAuthenticated && currentConversationId) {
        await loadConversations();
      }
    } catch (err: any) {
      console.error('Chat error:', err);
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.error ||
        err.message ||
        'An error occurred while sending the message. Please try again.';
      setError(errorMessage);
      
      setMessages([
        ...updatedMessages,
        {
          role: 'assistant',
          content: `Sorry, an error occurred: ${errorMessage}`,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = () => {
    setCurrentConversationId(null);
    setMessages([
      {
        role: 'assistant',
        content: 'Hello! I am an AI Assistant for Vietnamese students at Guangxi Normal University. I can help you answer questions about your studies, campus life, academic matters, and general inquiries. How can I assist you today?',
      },
    ]);
    setError(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex">
      {/* Sidebar - Conversation History */}
      {isAuthenticated && (
        <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
          <div className="p-4 border-b border-gray-200">
            <button
              onClick={handleNewConversation}
              className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Chat
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto">
            {isLoadingConversations ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : conversations.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No conversations yet. Start a new chat!
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {conversations.map((conv) => (
                  <div
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv.id)}
                    className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                      currentConversationId === conv.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm text-gray-900 truncate">
                          {conv.title}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {formatDate(conv.updated_at)}
                          {conv.message_count !== undefined && ` • ${conv.message_count} messages`}
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleDeleteConversation(e, conv.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors p-1"
                        title="Delete conversation"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md py-4 px-6">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">
                AI Assistant for Vietnamese Students
              </h1>
              <p className="text-sm text-blue-100 mt-1">
                Guangxi Normal University - Powered by Qwen-Max
              </p>
            </div>
            <button
              onClick={handleClearChat}
              className="px-4 py-2 text-sm bg-white bg-opacity-20 hover:bg-opacity-30 text-white rounded-lg transition-colors"
            >
              Clear Chat
            </button>
          </div>
        </div>

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-6">
          <div className="max-w-4xl mx-auto space-y-4">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-3xl rounded-lg px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-800 shadow-sm'
                  }`}
                >
                  <div className="whitespace-pre-wrap break-words">
                    {message.content}
                  </div>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-lg px-4 py-3 shadow-sm">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            </div>
          </div>
        )}

        {/* Selected Files Preview */}
        {selectedFiles.length > 0 && (
          <div className="bg-white border-t border-gray-200 py-2 px-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-600">Attached files:</span>
                {selectedFiles.map((file, index) => (
                  <div key={index} className="relative">
                    {file.type === 'pdf' ? (
                      <div className="w-16 h-16 bg-red-100 rounded border border-red-300 flex items-center justify-center">
                        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-16 h-16 bg-blue-100 rounded border border-blue-300 flex items-center justify-center">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                    )}
                    <button
                      onClick={() => removeFile(index)}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center hover:bg-red-600"
                    >
                      ×
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white text-xs px-1 truncate">
                      {file.name.length > 10 ? file.name.substring(0, 10) + '...' : file.name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="bg-white border-t border-gray-200 py-4 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-end space-x-3">
              <div className="flex-1">
                <textarea
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Enter your question or attach files..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                  rows={2}
                  disabled={isLoading}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,.pdf,.docx,.doc"
                  multiple
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                />
              </div>
              <div className="flex flex-col space-y-2">
                <label
                  htmlFor="file-upload"
                  className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer flex items-center justify-center h-[42px]"
                  title="Attach file (PDF or Word)"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                </label>
                <button
                  onClick={handleSend}
                  disabled={(!inputMessage.trim() && selectedFiles.length === 0) || isLoading}
                  className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium h-[42px] flex items-center justify-center"
                  title={isLoading ? 'Sending...' : 'Send'}
                >
                  {isLoading ? (
                    <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2 text-center">
              Press Enter to send, Shift+Enter for new line • Click 📎 to attach files (PDF or Word)
            </p>
            <p className="text-xs text-amber-600 mt-1 text-center font-medium">
              ⚠️ Note: Please do not upload images or PDF files containing only scanned images. Only text-based PDF and Word files are supported.
            </p>
            {!isAuthenticated && (
              <p className="text-xs text-blue-600 mt-1 text-center">
                💡 Login to save your chat history
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatbotPage;
